import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './CookieBanner.css';

export const COOKIE_CONSENT_KEY = 'cookie-consent';
export const COOKIE_CONSENT_VERSION = '2026-03-12-v2';

export interface CookiePreferences {
  version: string;
  essential: boolean; // always true
  analytics: boolean;
}

/** Read stored cookie preferences (returns null if none or outdated). */
export function getCookiePreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const prefs = JSON.parse(raw) as CookiePreferences;
    if (prefs.version !== COOKIE_CONSENT_VERSION) return null;
    return prefs;
  } catch {
    return null;
  }
}

/** Save cookie preferences and record consent on the server. */
export function saveCookiePreferences(prefs: CookiePreferences) {
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
  recordConsent(prefs);

  // If analytics was just enabled, load gtag dynamically
  if (prefs.analytics && !window.gtag) {
    loadGtag();
  }
  // If analytics was just disabled, revoke consent for GA
  if (!prefs.analytics && window.gtag) {
    window.gtag('consent', 'update', { analytics_storage: 'denied' });
  }
}

function loadGtag() {
  if (document.querySelector('script[src*="googletagmanager.com/gtag"]')) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=G-QLXZHDZW03';
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', 'G-QLXZHDZW03', { send_page_view: true });
}

async function recordConsent(prefs: CookiePreferences) {
  try {
    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    await axios.post(
      `${baseUrl}/consent`,
      {
        consentType: 'cookie',
        consentVersion: prefs.version,
        accepted: prefs.essential, // essential always true
        analytics: prefs.analytics,
      },
      { withCredentials: true }
    );
  } catch {
    // Don't block UX if consent recording fails
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const prefs = getCookiePreferences();
    if (!prefs) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    saveCookiePreferences({ version: COOKIE_CONSENT_VERSION, essential: true, analytics: true });
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    saveCookiePreferences({ version: COOKIE_CONSENT_VERSION, essential: true, analytics: false });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-modal="true" aria-label="Cookie consent">
      <div className="cookie-banner-inner">
        <p className="cookie-banner-text">
          Fox &amp; Quill uses essential cookies for login and optional analytics cookies to
          understand how visitors use the site.{' '}
          <Link to="/cookies">Learn more</Link>
        </p>
        <div className="cookie-banner-actions">
          <button className="cookie-btn cookie-btn-decline" onClick={handleEssentialOnly}>
            Essential Only
          </button>
          <button className="cookie-btn cookie-btn-accept" onClick={handleAcceptAll}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
